// Monthly calendar: arrivals, departures, onsite counts, leave and bookings per day.
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { Spinner, ErrorBox } from '../ui';
import { todayDarwin, addDays, addMonths, eachDate, dowOf, DOW, MONTHS, fullName, STATUS_COLOURS, paletteColour } from '../util';

export default function CalendarMonth() {
  const [month, setMonth] = useState(todayDarwin().slice(0, 7)); // YYYY-MM
  const [periods, setPeriods] = useState<any[] | null>(null);
  const [people, setPeople] = useState<any[]>([]);
  const [leave, setLeave] = useState<any[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api('/attendance'), api('/people'), api('/leave'), api('/bookings'), api('/work_areas')])
      .then(([a, p, l, b, w]) => { setPeriods(a); setPeople(p); setLeave(l); setBookings(b); setAreas(w); })
      .catch(e => setError(e.message));
  }, []);

  const names = useMemo(() => Object.fromEntries(people.map(p => [p.id, fullName(p)])), [people]);
  const areaName = useMemo(() => Object.fromEntries(areas.map(a => [a.id, a.name])), [areas]);
  const areaIds = useMemo(() => areas.map(a => a.id), [areas]);
  // Short label so it fits in a calendar cell, e.g. "Roofing and cladding" -> "Roofing…"
  const shortArea = (id: number | null) => {
    const n = areaName[id as number];
    if (!n) return '';
    return n.length > 14 ? n.slice(0, 13) + '…' : n;
  };

  if (error) return <ErrorBox error={error} />;
  if (!periods) return <Spinner />;

  const first = month + '-01';
  const last = addDays(addMonths(first, 1), -1);
  const gridStart = addDays(first, -((dowOf(first) + 6) % 7)); // Monday-start grid
  const gridEnd = addDays(last, 6 - ((dowOf(last) + 6) % 7));
  const days = eachDate(gridStart, gridEnd);
  const today = todayDarwin();
  const active = periods.filter(p => p.status !== 'cancelled');

  // How many people from each work area are onsite on a given date.
  function areaBreakdown(date: string): Record<string, number> {
    const eff = (p: any) => ({ ci: p.actual_check_in || p.check_in, co: p.actual_check_out || p.check_out });
    const out: Record<string, number> = {};
    for (const p of active) {
      if (date < eff(p).ci || date > eff(p).co) continue;
      const key = p.work_area_id ? String(p.work_area_id) : 'none';
      out[key] = (out[key] || 0) + 1;
    }
    return out;
  }

  return (
    <div className="page">
      <div className="page-head">
        <h2>Monthly calendar — {MONTHS[Number(month.slice(5)) - 1]} {month.slice(0, 4)}</h2>
        <div className="btn-row">
          <button className="btn" onClick={() => setMonth(addMonths(first, -1).slice(0, 7))}>← Previous</button>
          <button className="btn" onClick={() => setMonth(todayDarwin().slice(0, 7))}>This month</button>
          <button className="btn" onClick={() => setMonth(addMonths(first, 1).slice(0, 7))}>Next →</button>
        </div>
      </div>
      <div className="cal-grid">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <div key={d} className="cal-head">{d}</div>)}
        {days.map(d => {
          const eff = (p: any) => ({ ci: p.actual_check_in || p.check_in, co: p.actual_check_out || p.check_out });
          const arrivals = active.filter(p => eff(p).ci === d);
          const departures = active.filter(p => eff(p).co === d);
          const onsite = active.filter(p => d >= eff(p).ci && d <= eff(p).co).length;
          const onLeave = leave.filter(l => l.approval_status === 'approved' && d >= l.start_date && d <= l.end_date);
          const rooms = bookings.filter(b => !['cancelled'].includes(b.status) && d >= b.check_in && d < b.check_out)
            .reduce((s, b) => s + b.num_rooms, 0);
          return (
            <div key={d} className={'cal-cell' + (d.slice(0, 7) !== month ? ' other-month' : '') + (d === today ? ' today' : '')}>
              <div className="cal-date">{Number(d.slice(8))}{d === today ? ' • today' : ''}</div>
              {onsite > 0 && <span className="cal-dot" style={{ background: 'var(--olive)' }}>{onsite} onsite{rooms ? ` · ${rooms} rm` : ''}</span>}
              {/* Work area breakdown of who is onsite that day */}
              {Object.entries(areaBreakdown(d)).map(([aid, n]) => (
                <span key={'w' + aid} className="cal-dot"
                  style={{ background: paletteColour(aid === 'none' ? null : Number(aid), areaIds), opacity: .85 }}
                  title={aid === 'none' ? 'No work area assigned' : areaName[Number(aid)]}>
                  {aid === 'none' ? 'unassigned' : shortArea(Number(aid))} · {n}
                </span>
              ))}
              {arrivals.slice(0, 3).map(p => (
                <span key={'a' + p.id} className="cal-dot" style={{ background: STATUS_COLOURS.checked_in }}
                  title={`${names[p.person_id]} arriving${areaName[p.work_area_id] ? ' — ' + areaName[p.work_area_id] : ''}`}>
                  → {names[p.person_id]}{areaName[p.work_area_id] ? ` (${shortArea(p.work_area_id)})` : ''}
                </span>
              ))}
              {arrivals.length > 3 && <span className="muted">+{arrivals.length - 3} more arriving</span>}
              {departures.slice(0, 3).map(p => (
                <span key={'d' + p.id} className="cal-dot" style={{ background: STATUS_COLOURS.checked_out }}
                  title={`${names[p.person_id]} departing${areaName[p.work_area_id] ? ' — ' + areaName[p.work_area_id] : ''}`}>
                  ← {names[p.person_id]}{areaName[p.work_area_id] ? ` (${shortArea(p.work_area_id)})` : ''}
                </span>
              ))}
              {departures.length > 3 && <span className="muted">+{departures.length - 3} more departing</span>}
              {onLeave.slice(0, 2).map(l => (
                <span key={'l' + l.id} className="cal-dot" style={{ background: STATUS_COLOURS.on_leave }}>✈ {names[l.person_id]} leave</span>
              ))}
            </div>
          );
        })}
      </div>
      <p className="muted" style={{ marginTop: 8 }}>
        → arrival · ← departure · ✈ approved leave · rm = rooms booked that night.
        Coloured chips show how many people from each work area are onsite; hover any chip for the full name.
      </p>
    </div>
  );
}
